<template>
  <van-popup
    :value="present"
    position="bottom"
    :overlay="false"
    :duration="0"
    :lazy-render="false"
    :close-on-popstate="false"
    :safe-area-inset-bottom="false"
    get-container="body"
    ref="popup"
    class="mobile-chat-stage"
    :class="{ 'is-closing': closing }"
    @input="!$event && close()"
  >
    <div
      ref="backdrop"
      class="mobile-chat-sheet-overlay"
      aria-hidden="true"
      @click="close"
    />
    <div
      ref="surface"
      class="mobile-chat-sheet"
      :style="motionStyle"
      :class="{
        'is-expanded': expanded,
        'is-dragging': dragging,
        'is-viewport-adjusting': viewportAdjusting,
      }"
    >
      <section
        v-if="initialized && currentUserId"
        ref="dialog"
        class="sheet-content"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
        @keydown.capture="blockClosingKeys"
        @keydown.esc.stop.prevent="close"
        @keydown.tab="trapFocus"
      >
        <MobileChatHeader
          :title="title"
          :safe-top="sheetAtTop"
          sheet
          expandable
          :expanded="expanded"
          @back="close"
          @toggle-size="toggleSize"
        >

          <template #handle>
            <button
              type="button"
              class="sheet-grab"
              :aria-label="
                $t('chatWorkbench.resizeSheet', { percent: snapPercent })
              "
              :aria-expanded="String(expanded)"
              :aria-describedby="`sheet-grab-hint-${_uid}`"
              aria-keyshortcuts="ArrowUp ArrowDown Home End"
              @pointerdown.stop="startHandleDrag"
              @pointermove.stop="moveHandleDrag"
              @pointerup.stop="endHandleDrag"
              @pointercancel.stop="cancelHandleDrag"
              @lostpointercapture.stop="cancelHandleDrag"
              @click="cycleSnap"
              @keydown.up.stop.prevent="stepSnap(1)"
              @keydown.down.stop.prevent="stepSnap(-1)"
              @keydown.home.stop.prevent="setSnap(0)"
              @keydown.end.stop.prevent="setSnap(2)"
            >
              <span class="sheet-grab-bar" aria-hidden="true" />
            </button>
            <span :id="`sheet-grab-hint-${_uid}`" class="sheet-grab-hint">{{
              $t("chatWorkbench.resizeSheetHint")
            }}</span>
          </template>
        </MobileChatHeader>
        <ChatWorkbench
          :key="identity"
          ref="workspace"
          mobile
          external-header
          :visible="opened"
          @profile-change="profile = $event"
        />
      </section>
    </div>
  </van-popup>
</template>
<script>
import ChatWorkbench from "./ChatWorkbench.vue";
import MobileChatHeader from "./MobileChatHeader.vue";
import { resolveAgentServiceCurrentUserId } from "@/services/agent-service-state";
import { createMobileChatHistory } from "@/services/mobile-chat-history";
import { createMobileChatSheetMotion } from "@/services/mobile-chat-sheet-motion";
import {
  MOBILE_CHAT_SNAP_RATIOS,
  MOBILE_CHAT_DEFAULT_SNAP,
  MOBILE_CHAT_SNAP_DURATION,
  MOBILE_CHAT_SNAP_EASING,
  nextMobileChatSnap,
  mobileChatSheetHeight,
  mobileChatDragHeight,
  nearestMobileChatSnap,
} from "@/services/mobile-chat-sheet-snap";

/** 手机业务页专用半窗；与PC浮窗无关，展开也不导航消息中心。 */
export default {
  name: "MobileChatSheet",
  components: { ChatWorkbench, MobileChatHeader },
  data: () => ({
    opened: false,
    present: false,
    closing: false,
    entering: false,
    initialized: false,
    snapIndex: MOBILE_CHAT_DEFAULT_SNAP,
    tapDirection: 1,
    dragging: false,
    viewportAdjusting: false,
    keyboardRaised: false,
    profile: {},
    viewportHeight: 0,
    viewportTop: 0,
    openingHeight: 0,
  }),
  computed: {
    motionStyle() {
      return {
        "--sheet-motion-duration": MOBILE_CHAT_SNAP_DURATION + "ms",
        "--sheet-motion-easing": MOBILE_CHAT_SNAP_EASING,
      };
    },
    expanded() {
      return this.snapIndex === 2;
    },
    snapPercent() {
      return Math.round(MOBILE_CHAT_SNAP_RATIOS[this.snapIndex] * 100);
    },
    sheetAtTop() {
      return (
        this.viewportHeight > 0 &&
        mobileChatSheetHeight(
          this.snapIndex,
          this.viewportHeight,
          this.openingHeight,
          this.keyboardRaised
        ) >= this.viewportHeight
      );
    },
    currentUserId() {
      return resolveAgentServiceCurrentUserId(this.$store.state.user || {});
    },
    identity() {
      return (
        this.currentUserId +
        ":" +
        String(this.$store.state.user?.userInfo?.token || "")
      );
    },
    ui() {
      return this.$store.state.chatWorkbench;
    },
    requestVersion() {
      return this.ui.requestVersion;
    },
    title() {
      return this.profile.name || this.$t("chatWorkbench.title");
    },
    sheetStyle() {
      if (!this.viewportHeight) return null;
      const height = mobileChatSheetHeight(
        this.snapIndex,
        this.viewportHeight,
        this.openingHeight,
        this.keyboardRaised
      );
      return {
        "--sheet-height": height + "px",
        "--sheet-motion-duration": MOBILE_CHAT_SNAP_DURATION + "ms",
        "--sheet-motion-easing": MOBILE_CHAT_SNAP_EASING,
        "--sheet-viewport-bottom":
          this.viewportTop + this.viewportHeight + "px",
        height: "var(--sheet-height)",
        top: "calc(var(--sheet-viewport-bottom) - var(--sheet-height))",
        bottom: "auto",
      };
    },
  },
  watch: {
    requestVersion() {
      this.applyRequest();
    },
    "ui.open"(value) {
      if (!value) this.hide();
    },
    currentUserId() {
      this.$nextTick(this.applyRequest);
    },
    identity(value, previous) {
      // 首次补齐登录用户ID时保留待消费意图；真正换号/换Token才清理旧实例。
      if (String(previous || "").split(":")[0]) this.hide(true);
      this.initialized = false;
    },
    "$route.path"(value) {
      this.hide(true);
      if (value === "/myxg/messageCenter") this.initialized = false;
    },
  },
  mounted() {
    this._history = createMobileChatHistory(window, this.hide);
    this.updateViewport();
    window.visualViewport?.addEventListener("resize", this.updateViewport);
    window.visualViewport?.addEventListener("scroll", this.updateViewport);
    window.addEventListener("resize", this.updateViewport);
    this.applyRequest();
  },
  beforeDestroy() {
    this.hide(true);
    this._motion?.dispose();
    this.clearSheetMotion();
    this._history?.dispose();
    window.visualViewport?.removeEventListener("resize", this.updateViewport);
    window.visualViewport?.removeEventListener("scroll", this.updateViewport);
    window.removeEventListener("resize", this.updateViewport);
  },
  methods: {
    async applyRequest() {
      if (
        !this._history ||
        !this.currentUserId ||
        this.$route.path === "/myxg/messageCenter" ||
        !this.ui.open ||
        !this.ui.request ||
        this.ui.requestVersion === this.ui.handledMobileRequestVersion
      )
        return;
      const version = this.ui.requestVersion;
      const path = this.$route.path;
      this.$store.commit("chatWorkbench/acknowledgeMobileRequest", version);
      await this._history.open();
      if (version !== this.ui.requestVersion) return;
      if (!this.ui.open || this.$route.path !== path) {
        this._history.close();
        return;
      }
      const wasPresent = this.present;
      const entering = !this.opened || this.closing || !wasPresent;
      if (!wasPresent) this._returnFocus = document.activeElement;
      if (entering) {
        this.snapIndex = MOBILE_CHAT_DEFAULT_SNAP;
        this.tapDirection = 1;
        this.keyboardRaised = false;
        this.updateViewport();
        this.openingHeight = this.viewportHeight;
      }
      this.initialized = true;
      this.present = true;
      this.opened = true;
      this.closing = false;
      await this.$nextTick();
      if (version === this.ui.requestVersion && this.opened && this.ui.open) {
        if (entering) {
          const epoch = (this._presenceEpoch = (this._presenceEpoch || 0) + 1);
          const motion = this.ensureSheetMotion();
          this.entering = true;
          if (!wasPresent) motion?.prepare(this.targetGeometry());
          Promise.resolve(motion?.open(this.targetGeometry()) ?? true).then(
            (finished) => {
              if (
                finished &&
                epoch === this._presenceEpoch &&
                !this.closing &&
                this.opened
              ) {
                this.entering = false;
                this.focusHeader();
              }
            }
          );
        }
        if (this.$refs.workspace && this.ui.request.kind !== "INBOX")
          this.$refs.workspace.mobileDetail = true;
        await this.$refs.workspace?.openRequest(this.ui.request);
      }
    },
    hide(immediate = false) {
      if (this.closing && !immediate) return;
      this.clearSheetMotion();
      if (!this.opened && !this.present) {
        this.$store.commit("chatWorkbench/close");
        this._history?.close();
        return;
      }
      this.$refs.workspace?.captureReadingPosition();
      this.$store.commit("chatWorkbench/close");
      this._history?.close();
      this._presenceEpoch = (this._presenceEpoch || 0) + 1;
      this.entering = false;
      if (immediate) {
        this._motion?.dispose();
        this.completeClose(false);
        return;
      }
      this.closing = true;
      this._closeDeadline = this.motionNow() + MOBILE_CHAT_SNAP_DURATION;
      this.runCloseMotion();
    },
    motionNow() {
      return typeof performance !== "undefined"
        ? performance.now()
        : Date.now();
    },
    runCloseMotion() {
      const epoch = this._presenceEpoch,
        motion = this.ensureSheetMotion();
      if (!motion) {
        this.completeClose();
        return;
      }
      const remaining = Math.max(0, this._closeDeadline - this.motionNow());
      motion.close(remaining).then((finished) => {
        if (finished && epoch === this._presenceEpoch && this.closing)
          this.completeClose();
      });
    },
    completeClose(restoreFocus = true) {
      this.opened = false;
      this.present = false;
      this.closing = false;
      this.entering = false;
      if (this.$refs.dialog?.contains?.(document.activeElement))
        document.activeElement?.blur?.();
      if (restoreFocus)
        this.$nextTick(() => {
          if (this._returnFocus?.isConnected)
            this._returnFocus.focus({ preventScroll: true });
        });
    },
    blockClosingKeys(event) {
      if (this.closing) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    targetGeometry(index = this.snapIndex) {
      const height = mobileChatSheetHeight(
        index,
        this.viewportHeight,
        this.openingHeight,
        this.keyboardRaised
      );
      return {
        height,
        top: this.viewportTop + this.viewportHeight - height,
        radius: index === 2 ? 0 : 16,
      };
    },
    ensureSheetMotion() {
      if (this._motion) return this._motion;
      const panel = this.popupElement(),
        backdrop = this.$refs.backdrop;
      if (!panel || !backdrop) return null;
      this._motion = createMobileChatSheetMotion({
        panel,
        backdrop,
        viewport: () => ({ bottom: this.viewportTop + this.viewportHeight }),
        reducedMotion: () => this.reducedMotion(),
        clock: window,
      });
      return this._motion;
    },
    close() {
      this.hide();
    },
    toggleSize() {
      this.setSnap(this.expanded ? MOBILE_CHAT_DEFAULT_SNAP : 2);
    },
    popupElement() {
      return this.$refs.surface || this.$refs.popup?.$el || this.$refs.popup;
    },
    targetSheetHeight() {
      return mobileChatSheetHeight(
        this.snapIndex,
        this.viewportHeight,
        this.openingHeight,
        this.keyboardRaised
      );
    },
    writeSheetHeight(height) {
      const panel = this.popupElement();
      if (!panel?.style) return;
      panel.style.setProperty("--sheet-height", Math.round(height) + "px");
      panel.style.height = Math.round(height) + "px";
      panel.style.top = this.viewportTop + this.viewportHeight - height + "px";
    },
    reducedMotion() {
      return (
        typeof window !== "undefined" &&
        Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches)
      );
    },
    setSnap(index, capture = true) {
      if (!this.opened || this.closing) return;
      const epoch = (this._snapEpoch = (this._snapEpoch || 0) + 1);
      const next = Math.max(0, Math.min(2, index));
      if (capture) this.$refs.workspace?.captureReadingPosition();
      const previous = this.snapIndex;
      this.releaseSheetPointer();
      if (next === 2) this.tapDirection = -1;
      else if (next === 0) this.tapDirection = 1;
      else if (next !== previous) this.tapDirection = next > previous ? 1 : -1;
      this.snapIndex = next;
      this._pendingResize = true;
      const motion = this.ensureSheetMotion();
      if (!motion) {
        this.writeSheetHeight(this.targetSheetHeight());
        this.entering = false;
        this.finishSnapTransition();
        return;
      }
      motion.resize(this.targetGeometry()).then((finished) => {
        if (finished && epoch === this._snapEpoch && !this.closing) {
          this.entering = false;
          this.finishSnapTransition();
        }
      });
    },
    cycleSnap(event) {
      if (this._ignoreHandleClick && event?.detail !== 0) {
        this._ignoreHandleClick = false;
        return;
      }
      this._ignoreHandleClick = false;
      const next = nextMobileChatSnap(this.snapIndex, this.tapDirection);
      this.tapDirection = next.direction;
      this.setSnap(next.index);
    },
    stepSnap(direction) {
      this.setSnap(this.snapIndex + direction);
    },
    finishSnapTransition(event) {
      if (
        event &&
        (event.target !== this.popupElement() ||
          event.propertyName !== "height")
      )
        return;
      if (!this._pendingResize) return;
      clearTimeout(this._snapTimer);
      this._snapTimer = null;
      this._pendingResize = false;
      if (this.opened) this.$refs.workspace?.restoreReadingPosition();
    },
    startHandleDrag(event) {
      if (
        !this.opened ||
        this.closing ||
        this.entering ||
        this.keyboardRaised ||
        event.isPrimary === false ||
        (event.button != null && event.button !== 0)
      )
        return;
      this.releaseSheetPointer();
      this._ignoreHandleClick = false;
      this._motion?.freeze();
      const popup = this.popupElement();
      const height =
        popup?.getBoundingClientRect?.().height || this.targetSheetHeight();
      this._sheetDrag = {
        id: event.pointerId,
        target: event.currentTarget,
        startY: event.clientY,
        startX: event.clientX,
        height,
        currentHeight: height,
        lastY: event.clientY,
        lastTime: event.timeStamp,
        velocity: 0,
        moved: false,
      };
      try {
        event.currentTarget.setPointerCapture?.(event.pointerId);
      } catch {
        /* 旧WebView不支持捕获时仍使用按钮事件。 */
      }
    },
    moveHandleDrag(event) {
      const drag = this._sheetDrag;
      if (!drag || drag.id !== event.pointerId) return;
      const distance = event.clientY - drag.startY;
      if (!drag.moved && Math.abs(distance) < 8) return;
      if (
        !drag.moved &&
        Math.abs(event.clientX - drag.startX) > Math.abs(distance)
      ) {
        this.cancelHandleDrag(event);
        return;
      }
      if (!drag.moved) {
        this.$refs.workspace?.captureReadingPosition();
        this.dragging = true;
        drag.moved = true;
      }
      const elapsed = event.timeStamp - drag.lastTime;
      if (elapsed > 0)
        drag.velocity =
          0.6 * ((event.clientY - drag.lastY) / elapsed) + 0.4 * drag.velocity;
      drag.lastY = event.clientY;
      drag.lastTime = event.timeStamp;
      drag.currentHeight = mobileChatDragHeight(
        drag.height,
        drag.startY,
        event.clientY,
        this.viewportHeight
      );
      // Pointer事件可高于屏幕刷新率；一帧最多写一个局部CSS变量，不更新Vue消息树。
      if (this._dragFrame == null) {
        const frame =
          typeof window !== "undefined" && window.requestAnimationFrame
            ? window.requestAnimationFrame.bind(window)
            : (fn) => setTimeout(fn, 16);
        this._dragFrame = frame(() => {
          this._dragFrame = null;
          if (this._sheetDrag === drag)
            this.writeSheetHeight(drag.currentHeight);
        });
      }
    },
    endHandleDrag(event) {
      const drag = this._sheetDrag;
      if (!drag || drag.id !== event.pointerId) return;
      const moved = drag.moved;
      const velocity =
        event.timeStamp - drag.lastTime > 120 ? 0 : drag.velocity;
      const index = nearestMobileChatSnap(
        drag.currentHeight,
        this.viewportHeight,
        velocity
      );
      this.releaseSheetPointer();
      if (moved) {
        this._ignoreHandleClick = true;
        this.setSnap(index, false);
      }
    },
    cancelHandleDrag(event) {
      const drag = this._sheetDrag;
      if (!drag || (event && event.pointerId !== drag.id)) return;
      this.releaseSheetPointer();
      if (drag.moved) {
        this._ignoreHandleClick = true;
        this.setSnap(this.snapIndex, false);
      }
    },
    releaseSheetPointer() {
      const drag = this._sheetDrag;
      this._sheetDrag = null;
      this.dragging = false;
      if (this._dragFrame != null) {
        if (typeof window !== "undefined" && window.cancelAnimationFrame)
          window.cancelAnimationFrame(this._dragFrame);
        else clearTimeout(this._dragFrame);
        this._dragFrame = null;
      }
      if (drag?.target?.hasPointerCapture?.(drag.id))
        drag.target.releasePointerCapture(drag.id);
    },
    clearSheetMotion() {
      this._snapEpoch = (this._snapEpoch || 0) + 1;
      this.releaseSheetPointer();
      clearTimeout(this._snapTimer);
      clearTimeout(this._viewportTimer);
      this._snapTimer = null;
      this._viewportTimer = null;
      this._pendingResize = false;
      this.viewportAdjusting = false;
    },
    focusHeader() {
      this.$refs.dialog
        ?.querySelector("button")
        ?.focus({ preventScroll: true });
    },
    trapFocus(event) {
      const nodes = Array.from(
        this.$refs.dialog?.querySelectorAll(
          'button:not(:disabled),textarea:not(:disabled),input:not(:disabled),a[href],[tabindex="0"]'
        ) || []
      ).filter((node) => node.getClientRects().length);
      if (!nodes.length) return;
      const first = nodes[0],
        last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    updateViewport() {
      const height = Math.round(
        window.visualViewport?.height || window.innerHeight
      );
      const width = Math.round(
        window.innerWidth || window.visualViewport?.width || 0
      );
      const top = Math.max(
        0,
        Math.round(window.visualViewport?.offsetTop || 0)
      );
      const input =
        typeof document !== "undefined" ? document.activeElement : null;
      const editing = Boolean(
        input &&
          this.$refs.dialog?.contains(input) &&
          input.matches?.('textarea,input,[contenteditable="true"]')
      );
      const rotated =
        this._viewportWidth && Math.abs(width - this._viewportWidth) > 60;
      const geometryChanged =
        height !== this.viewportHeight ||
        top !== this.viewportTop ||
        Boolean(rotated);
      if (
        this.opened &&
        (height !== this.viewportHeight || top !== this.viewportTop)
      ) {
        this.cancelHandleDrag();
        this.viewportAdjusting = true;
        clearTimeout(this._viewportTimer);
        this._viewportTimer = setTimeout(() => {
          this.viewportAdjusting = false;
        }, 100);
        if (rotated) {
          this.openingHeight = editing
            ? Math.max(height, window.innerHeight)
            : height;
          this.keyboardRaised = editing && this.openingHeight - height > 120;
        } else if (editing && this.openingHeight - height > 120)
          this.keyboardRaised = true;
        else if (height >= this.openingHeight - 80) {
          this.keyboardRaised = false;
          this.openingHeight = height;
        } else if (!editing && !this.keyboardRaised)
          this.openingHeight = height;
      }
      this._viewportWidth = width;
      this.viewportHeight = height;
      this.viewportTop = top;
      if (this.closing && geometryChanged) this.runCloseMotion();
      else if (this.opened && this._motion && geometryChanged) {
        this._motion.setGeometry(this.targetGeometry());
        this.entering = false;
        this.finishSnapTransition();
      }
    },
  },
};
</script>
<style lang="less">
.mobile-chat-stage.van-popup {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  max-width: none;
  max-height: none;
  margin: 0;
  transform: none !important;
  border-radius: 0 !important;
  background: transparent;
  overflow: hidden;
  transition: none !important;
  &.is-closing .mobile-chat-sheet {
    pointer-events: none;
  }
}
.mobile-chat-sheet {
  position: absolute;
  left: 50%;
  width: 100%;
  max-width: 630px;
  transform: translate3d(-50%, 100vh, 0);
  border-radius: 16px 16px 0 0;
  height: 80vh;
  height: 80dvh;
  overflow: hidden;
  background: #fff;
  contain: layout style;
  &.is-dragging,
  &.is-viewport-adjusting {
    transition: none !important;
    .mobile-chat-header,
    .sheet-grab {
      transition: none;
    }
  }
  &.is-expanded {
    border-radius: 0;
  }
  .sheet-content {
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;
  }
  .sheet-grab {
    position: absolute;
    z-index: 1;
    top: var(--mobile-chat-safe-top, 0px);
    left: 60px;
    right: 60px;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    height: 44px;
    min-width: 44px;
    box-sizing: border-box;
    padding: 5px 0 0;
    border: 0;
    background: transparent;
    cursor: ns-resize;
    touch-action: none;
    user-select: none;
    -webkit-user-select: none;
    transition: top var(--sheet-motion-duration, 240ms)
      var(--sheet-motion-easing, cubic-bezier(0.2, 0.8, 0.2, 1));
    .sheet-grab-bar {
      display: block;
      width: 36px;
      height: 4px;
      border-radius: 3px;
      background: #fff;
      border: 1px solid #cbd5e1;
      box-shadow: 0 1px 3px rgba(15, 23, 42, 0.1);
    }
    &:focus-visible {
      outline: 2px solid #4286f5;
      outline-offset: -3px;
      border-radius: 8px;
    }
  }
  .sheet-grab-hint {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
  @media (prefers-reduced-motion: reduce) {
    &,
    &.van-popup {
      transition-duration: 0s !important;
    }
    .sheet-grab {
      transition: none;
    }
  }
}
.mobile-chat-sheet-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.32);
  opacity: 0;
}
</style>
